import { getStoredValue, persistentAtom } from './persistence';
import { Logs } from '@/data/logs';
import { atom } from 'nanostores';
import { useEffect, useState } from 'react';

/**
 * Core Logs instance - persisted to localStorage
 * This is NOT an atom because the Logs class manages its own subscriptions
 * and state mutations through emitChange()
 */
const initialLogsData = getStoredValue<unknown[]>('logs', []);
let initialLogs: Logs;
try {
  initialLogs = Logs.fromJSON(initialLogsData);
} catch (error) {
  console.warn('Error loading logs from localStorage:', error);
  initialLogs = new Logs();
}

export const logs = initialLogs;

/**
 * Internal notifier atom - incremented whenever logs change
 * This allows computed stores to depend on logs changes
 * without needing to import the logs instance directly
 */
export const $logsVersion = atom(0);

// Set up persistence of logs when they change
let isUpdatingFromStorage = false;
logs.subscribe(() => {
  if (isUpdatingFromStorage) return;

  // Trigger downstream computed stores
  $logsVersion.set($logsVersion.get() + 1);

  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(
        'logbook-logs',
        JSON.stringify(logs.toJSON()),
      );
    } catch (error) {
      console.warn('Error persisting logs to localStorage:', error);
    }
  }
});

// Listen for storage changes from other tabs
if (typeof window !== 'undefined') {
  window.addEventListener('storage', event => {
    if (event.key === 'logbook-logs' && event.newValue) {
      try {
        isUpdatingFromStorage = true;
        const data = JSON.parse(event.newValue);
        logs.loadFromJSON(data);
        $logsVersion.set($logsVersion.get() + 1);
        isUpdatingFromStorage = false;
      } catch (error) {
        console.warn('Error syncing logs from localStorage:', error);
        isUpdatingFromStorage = false;
      }
    }
  });
}

/**
 * Hook to subscribe to Logs changes
 * Components that need to re-render when logs change should use this
 */
export function useLogs() {
  const [, setTick] = useState(0);

  useEffect(() => {
    const unsubscribe = logs.subscribe(() => {
      setTick(tick => tick + 1);
    });

    return unsubscribe;
  }, []);

  return logs;
}

/**
 * Code words - per logbook/mission
 */
export const $codeWords = persistentAtom<Record<string, string>>(
  'codeWords',
  {},
);
